"use client";

import React from "react";
import StickyBox from "react-sticky-box";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
	Box,
	Card,
	Stack,
	SvgIcon,
	Typography,
	CardContent,
	Grid,
} from "@mui/material";

import { WizardSchemaType } from "../index";
import { ImagePlaceholderSvg } from "@onair/ui/svg/image-placeholder";
import { FileThumbnail, fileData } from "@onair/minimal-ui/file-thumbnail";
import TextMaxLine from "@onair/ui/text-max-line";
import { EVENT_COLORS } from "@onair/ui/event/constants";
import { formatData } from "@onair/utils/format-number";
import AgendaPreviewItem from "./agenda-item";

interface PropTypes {
	data: WizardSchemaType;
}

const PreviewResource: React.FC<PropTypes> = (props) => {
	const { data } = props;

	const colors = EVENT_COLORS;

	const docs = React.useMemo(() => {
		return data.stepResource?.docs && data.stepResource?.docs.length > 0 ? (
			<>
				<Typography variant="h6" color={colors.color}>
					Tài liệu
				</Typography>
				<Grid container spacing={2}>
					{data.stepResource?.docs.map((file, index) => {
						const { name, size } = fileData(file);
						return (
							<Grid item key={index} md={3}>
								<Box
									position="relative"
									height="100%"
									display="flex"
									sx={{
										flexDirection: {
											xs: "row",
											md: "column",
										},
										gap: {
											xs: 1,
											md: 0.5,
										},
										alignItems: {
											xs: "center",
											md: "unset",
										},
									}}
									className="rounded-lg"
									bgcolor={colors.bgColor}
									border="1px solid"
									borderColor={colors.borderColor}
									py={1.5}
									px={1}
								>
									<Box display="flex" alignSelf="start">
										<FileThumbnail file={file} />
									</Box>
									<TextMaxLine
										line={1}
										flexGrow={1}
										fontSize={12}
										variant="subtitle1"
										color={colors.color}
										sx={{
											wordWrap: "break-word",
										}}
									>
										{name}
									</TextMaxLine>
									<Typography
										fontSize={10}
										variant="caption"
										color={colors.color}
									>
										{formatData(size)}
									</Typography>
								</Box>
							</Grid>
						);
					})}
				</Grid>
			</>
		) : (
			<>
				<Box
					className="w-1/4 h-5 rounded-lg"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				/>
				<Grid container spacing={1}>
					{[1, 2, 3, 4].map((_, i) => (
						<Grid key={i} item xs={3}>
							<Stack
								spacing={0.5}
								p={1}
								direction="column"
								className="rounded-lg"
								bgcolor={colors.bgColor}
								border={`1px solid ${colors.borderColor}`}
							>
								<Box
									className="size-9 rounded"
									bgcolor={colors.bgLabelColor}
									border="1px solid"
									borderColor="divider"
								/>
								<Box
									className="h-4 w-full rounded"
									bgcolor={colors.bgLabelColor}
									border="1px solid"
									borderColor="divider"
								/>
								<Box
									className="h-3 w-1/2 rounded"
									bgcolor={colors.bgLabelColor}
									border="1px solid"
									borderColor="divider"
								/>
							</Stack>
						</Grid>
					))}
				</Grid>
			</>
		);
	}, [colors.bgColor, colors.borderColor, data.stepResource?.docs]);

	return (
		<Box
			flex={1}
			maxWidth="50%"
			mx="auto"
			display={{ xs: "none", md: "block" }}
		>
			<StickyBox className="stickyBox" offsetTop={100} offsetBottom={20}>
				<Card>
					<Stack bgcolor={colors.bgLightColor} p={2} spacing={2}>
						<Stack direction="row" spacing={1}>
							<Box className="size-3 rounded-full bg-white" />
							<Box className="size-3 rounded-full bg-white" />
							<Box className="size-3 rounded-full bg-white" />
						</Stack>
						<Stack direction="row" spacing={1} alignItems="center">
							<Stack direction="row" spacing={1} alignItems="center" flex={1}>
								<Box className="w-[20%] h-5 rounded bg-white" />
								<Box className="w-[60%] h-5 rounded bg-white" />
							</Stack>
							<Stack direction="row" spacing={1} alignItems="center">
								<Box className="size-4 rounded-full bg-white" />
								<Box className="size-4 rounded-full bg-white" />
								<Box className="size-4 rounded-full bg-white" />
								<Box className="size-6 rounded-full bg-white" />
							</Stack>
						</Stack>
					</Stack>
					<CardContent>
						<Stack spacing={3}>
							<Stack px={2} spacing={2}>
								{(data.stepResource?.agendas || []).length > 0 ? (
									<Typography align="center" variant="h6" color={colors.color}>
										Lịch trình sự kiện
									</Typography>
								) : (
									<Box
										className="w-1/3 mx-auto h-5 rounded-lg"
										bgcolor={colors.bgColor}
										border={`1px solid ${colors.borderColor}`}
									/>
								)}
								<Grid px={2} container spacing={2}>
									{(data.stepResource?.agendas || []).length > 0 ? (
										<>
											{data.stepResource?.agendas.map((agenda, i) => {
												return <AgendaPreviewItem data={agenda} key={i} />;
											})}
										</>
									) : (
										<Grid item xs={12}>
											<Grid container spacing={1.5}>
												<Grid item xs={3}>
													<Stack spacing={1}>
														<Box
															className="w-full h-5 rounded-lg"
															bgcolor={colors.bgColor}
															border={`1px solid ${colors.borderColor}`}
														/>
														<Box
															display="flex"
															alignItems="center"
															justifyContent="center"
															className="w-full h-[120px] rounded-lg"
															bgcolor={colors.bgColor}
															border={`1px solid ${colors.borderColor}`}
														>
															<ImagePlaceholderSvg />
														</Box>
													</Stack>
												</Grid>
												<Grid item xs={8}>
													<Stack spacing={1}>
														<Box
															className="w-full h-5 rounded-lg"
															bgcolor={colors.bgColor}
															border={`1px solid ${colors.borderColor}`}
														/>
														<Box
															display="flex"
															alignItems="center"
															justifyContent="center"
															className="w-full h-[120px] rounded-lg"
															bgcolor={colors.bgColor}
															border={`1px solid ${colors.borderColor}`}
														/>
													</Stack>
												</Grid>
												<Grid item xs={1}>
													<Box
														className="size-5 rounded-full"
														bgcolor={colors.bgColor}
														border={`1px solid ${colors.borderColor}`}
													/>
												</Grid>
											</Grid>
										</Grid>
									)}
								</Grid>
							</Stack>
							<Stack spacing={2}>{docs}</Stack>
							<Stack direction="row">
								{(data.stepResource?.whyChooses || []).length === 0 ? (
									<Box
										className="w-1/4 h-5 rounded-lg"
										bgcolor={colors.bgColor}
										border={`1px solid ${colors.borderColor}`}
									/>
								) : (
									<Box className="w-1/4">
										<Typography
											className="block"
											variant="h6"
											color={colors.color}
										>
											Tại sao nên tham gia sự kiện?
										</Typography>
									</Box>
								)}
								{data.stepResource?.whyChooses &&
								data.stepResource?.whyChooses.length > 0 ? (
									<Grid ml={5} container spacing={1}>
										{data.stepResource?.whyChooses.map((el, i) => {
											if (!el) {
												return null;
											}
											return (
												<Grid item key={i} xs={6}>
													<Stack
														direction="column"
														spacing={1}
														p={1}
														className="rounded-lg"
														bgcolor={colors.bgColor}
														border={`1px solid`}
														borderColor={colors.borderColor}
													>
														<SvgIcon>
															<svg
																width={40}
																height={40}
																viewBox="0 0 40 40"
																fill="none"
																xmlns="http://www.w3.org/2000/svg"
															>
																<rect
																	width={40}
																	height={40}
																	rx={20}
																	fill="#FFAB00"
																/>
																<path
																	d="M28.2629 16.3756C28.1817 16.2739 28.0834 16.2939 28.0318 16.3146C27.9886 16.3321 27.8896 16.3881 27.9033 16.5283C27.9198 16.6967 27.929 16.8684 27.9308 17.0387C27.9381 17.7451 27.6547 18.4372 27.1533 18.9375C26.6551 19.4345 25.9999 19.7023 25.3021 19.6945C24.3491 19.6824 23.5586 19.1853 23.0161 18.257C22.5676 17.4894 22.7647 16.4994 22.9735 15.4511C23.0956 14.8376 23.2219 14.2031 23.2219 13.5993C23.2219 8.89758 20.0611 6.18502 18.177 4.98657C18.138 4.96182 18.101 4.95312 18.0681 4.95312C18.0147 4.95312 17.9723 4.97617 17.9515 4.99027C17.911 5.01766 17.8463 5.08009 17.8671 5.19059C18.5873 9.0149 16.4393 11.315 14.1651 13.7501C11.821 16.2602 9.16406 19.1052 9.16406 24.2361C9.16406 30.198 14.0144 35.0483 19.9763 35.0483C24.885 35.0483 29.213 31.626 30.501 26.7257C31.3793 23.3845 30.4589 19.1284 28.2629 16.3756ZM20.246 32.7405C18.7531 32.8085 17.3334 32.2731 16.2489 31.2362C15.1761 30.2104 14.5608 28.7789 14.5608 27.3085C14.5608 24.5494 15.6158 22.5238 18.4533 19.8348C18.4998 19.7908 18.5473 19.7768 18.5888 19.7768C18.6263 19.7768 18.6589 19.7883 18.6813 19.7991C18.7285 19.8218 18.8061 19.8781 18.7956 19.9999C18.6942 21.1805 18.6959 22.1604 18.8008 22.9125C19.0688 24.8337 20.4752 26.1246 22.3006 26.1246C23.1956 26.1246 24.0481 25.7878 24.7011 25.1762C24.7768 25.1052 24.8615 25.1142 24.8939 25.1211C24.9369 25.1304 24.9944 25.1567 25.0246 25.2295C25.2953 25.8831 25.4337 26.577 25.4358 27.2917C25.4444 30.1675 23.1163 32.6119 20.246 32.7405Z"
																	fill="white"
																/>
															</svg>
														</SvgIcon>
														{!el ? (
															<Box className="h-5 w-full rounded bg-white" />
														) : (
															<Typography
																variant="body2"
																fontSize={12}
																color={colors.color}
																sx={{
																	wordBreak: "break-all",
																}}
															>
																{el}
															</Typography>
														)}
													</Stack>
												</Grid>
											);
										})}
									</Grid>
								) : (
									<Grid ml={5} container spacing={1}>
										{[1, 2, 3, 4].map((_, i) => (
											<Grid item key={i} xs={6}>
												<Stack
													p={1}
													className="rounded-lg"
													bgcolor={colors.bgColor}
													border={`1px solid ${colors.borderColor}`}
													spacing={1}
												>
													<Box
														className="size-7 rounded-full"
														bgcolor={colors.bgLabelColor}
														border={`1px solid`}
														borderColor="divider"
													/>
													<Box
														className="h-5 w-full rounded"
														bgcolor={colors.bgLabelColor}
														border={`1px solid`}
														borderColor="divider"
													/>
												</Stack>
											</Grid>
										))}
									</Grid>
								)}
							</Stack>
							<Stack spacing={2}>
								{(data.stepResource?.objects || []).length === 0 ? (
									<Box
										className="w-1/5 h-5 rounded-lg"
										bgcolor={colors.bgColor}
										border={`1px solid ${colors.borderColor}`}
									/>
								) : (
									<Typography variant="h6" color={colors.color}>
										Sự kiện dành cho ai?
									</Typography>
								)}
								{data.stepResource?.objects &&
								data.stepResource?.objects.length > 0 ? (
									<Stack direction="row" spacing={2} flexWrap="wrap">
										{data.stepResource?.objects.map((el, i) => {
											if (!el) {
												return null;
											}
											return (
												<Stack
													key={i}
													spacing={1}
													p={1}
													className="w-[48%] rounded-lg"
													bgcolor={colors.bgColor}
													border={`1px solid ${colors.borderColor}`}
												>
													<SvgIcon>
														<svg
															width={42}
															height={42}
															viewBox="0 0 42 42"
															fill="none"
															xmlns="http://www.w3.org/2000/svg"
														>
															<rect
																width={42}
																height={42}
																rx={6}
																fill="#0050FF"
																fillOpacity={0.08}
															/>
															<path
																opacity={0.32}
																d="M11.281 28.5149C11.3697 29.3735 11.9326 30.0532 12.7769 30.2329C14.1095 30.5166 16.6216 30.8574 21 30.8574C25.3784 30.8574 27.8905 30.5166 29.2232 30.2329C30.0674 30.0532 30.6304 29.3735 30.719 28.5149C30.8505 27.2418 31 24.9044 31 20.8574C31 16.8105 30.8505 14.473 30.719 13.1999C30.6304 12.3413 30.0674 11.6617 29.2231 11.4819C27.8905 11.1982 25.3784 10.8574 21 10.8574C16.6216 10.8574 14.1095 11.1982 12.7769 11.4819C11.9326 11.6617 11.3697 12.3413 11.281 13.1999C11.1495 14.473 11 16.8105 11 20.8574C11 24.9044 11.1495 27.2418 11.281 28.5149Z"
																fill="#0050FF"
																fillOpacity={0.48}
															/>
															<path
																d="M22.9382 22.7133C24.263 22.0157 25.1663 20.6254 25.1663 19.0241C25.1663 16.7229 23.3008 14.8574 20.9996 14.8574C18.6984 14.8574 16.8329 16.7229 16.8329 19.0241C16.8329 20.6254 17.7363 22.0158 19.0612 22.7134C17.2869 23.3894 15.9322 24.9666 15.5125 26.9103C15.4545 27.1793 15.6025 27.4555 15.8734 27.5045C16.8458 27.6805 18.4562 27.8574 21.0006 27.8574C23.545 27.8574 25.1554 27.6805 26.1278 27.5045C26.3977 27.4557 26.5454 27.1806 26.4876 26.9125C26.0685 24.9677 24.7133 23.3895 22.9382 22.7133Z"
																fill="#0050FF"
																fillOpacity={0.48}
															/>
														</svg>
													</SvgIcon>
													{!el ? (
														<Box className="h-5 w-full rounded bg-white" />
													) : (
														<Typography
															variant="body2"
															color={colors.color}
															fontSize={12}
														>
															{el}
														</Typography>
													)}
												</Stack>
											);
										})}
									</Stack>
								) : (
									<Grid container spacing={1}>
										{[1, 2, 3, 4].map((_, i) => (
											<Grid item key={i} xs={3}>
												<Stack
													spacing={1}
													p={1}
													className="rounded-lg"
													bgcolor={colors.bgColor}
													border={`1px solid ${colors.borderColor}`}
												>
													<Box
														className="size-7 rounded-full"
														bgcolor={colors.bgLabelColor}
														border={`1px solid`}
														borderColor="divider"
													/>
													<Box
														className="h-5 w-full rounded"
														bgcolor={colors.bgLabelColor}
														border={`1px solid`}
														borderColor="divider"
													/>
												</Stack>
											</Grid>
										))}
									</Grid>
								)}
							</Stack>
							<Stack direction="row">
								{(data.stepResource?.qas || []).length === 0 ? (
									<Box
										className="w-1/4 h-5 rounded-lg"
										bgcolor={colors.bgColor}
										border={`1px solid ${colors.borderColor}`}
									/>
								) : (
									<Typography variant="h6" color={colors.color}>
										Câu hỏi thường gặp
									</Typography>
								)}
								{data.stepResource?.qas && data.stepResource?.qas.length > 0 ? (
									<Grid ml={5} container spacing={2}>
										{data.stepResource?.qas.map((el, i) => {
											if (!el) {
												return null;
											}
											return (
												<Grid item xs={12} key={i}>
													<Stack
														spacing={1}
														p={1}
														className="rounded-lg"
														bgcolor={colors.bgColor}
														border={`1px solid ${colors.borderColor}`}
													>
														{!el.question ? (
															<Stack
																direction="row"
																justifyContent="space-between"
																spacing={2}
															>
																<Box
																	className="h-4 w-1/4 rounded"
																	bgcolor={colors.bgLabelColor}
																	border="1px solid"
																	borderColor="divider"
																/>
																<Box
																	className="size-4 rounded-full"
																	bgcolor={colors.bgLabelColor}
																	border="1px solid"
																	borderColor="divider"
																/>
															</Stack>
														) : (
															<Stack
																direction="row"
																spacing={1}
																alignItems="center"
															>
																<Typography
																	flex={1}
																	variant="subtitle2"
																	color={colors.color}
																	fontSize={12}
																>
																	{el.question}
																</Typography>
																<KeyboardArrowDownIcon
																	fontSize="small"
																	sx={{
																		color: colors.color,
																	}}
																/>
															</Stack>
														)}
														{!el.answer ? (
															<Box
																className="h-10 w-full rounded"
																bgcolor={colors.bgLabelColor}
																border="1px solid"
																borderColor="divider"
															/>
														) : (
															<Typography
																variant="body2"
																color={colors.color}
																fontSize={12}
															>
																{el.answer}
															</Typography>
														)}
													</Stack>
												</Grid>
											);
										})}
									</Grid>
								) : (
									<Grid ml={5} container spacing={2}>
										{[1, 2].map((_, i) => (
											<Grid key={i} item xs={12}>
												<Stack
													spacing={1}
													p={1}
													className="w-full rounded-lg"
													bgcolor={colors.bgColor}
													border={`1px solid ${colors.borderColor}`}
												>
													<Stack
														direction="row"
														justifyContent="space-between"
														spacing={2}
													>
														<Box
															className="h-4 w-1/4 rounded"
															bgcolor={colors.bgLabelColor}
															border="1px solid"
															borderColor="divider"
														/>
														<Box
															className="size-4 rounded-full"
															bgcolor={colors.bgLabelColor}
															border="1px solid"
															borderColor="divider"
														/>
													</Stack>
													<Box
														className="h-10 w-full rounded"
														bgcolor={colors.bgLabelColor}
														border="1px solid"
														borderColor="divider"
													/>
												</Stack>
											</Grid>
										))}
									</Grid>
								)}
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			</StickyBox>
		</Box>
	);
};

export default PreviewResource;
